import React from 'react'
import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
// imported straight from the dialog, not the dialogs barrel — the barrel pulls the whole
// presentation editor in behind it, and this component renders in the customer view
import PresentationTermsDialog from '../../dialogs/PresentationTermsDialog'
import { usePresentationMoreActions } from '../PresentationAcceptButton/usePresentationMoreActions'

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        flex: '0 0 auto',
        padding: theme.spacing(1),
        borderTop: `0.8px solid ${neutral.P90}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        ['@media print']: {
          display: 'none'
        }
      },
      action: {
        minHeight: 44,
        padding: theme.spacing(0, 1.5),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        border: 'none',
        borderRadius: 8,
        background: 'transparent',
        cursor: 'pointer',
        font: 'inherit',
        fontSize: theme.typography.pxToRem(15),
        color: '#354962',
        textAlign: 'left',
        WebkitTapHighlightColor: 'transparent',
        '&:hover, &:active': {
          backgroundColor: neutral.P96
        }
      },
      icon: {
        fontSize: 18,
        color: neutral.P50
      }
    }
  },
  { name: 'PresentationDrawerActions' }
)

export interface PresentationDrawerActionsProps {
  /**
   * Called before an action runs, so the drawer can close rather than sit over the print dialog.
   * Not called for View Terms: the drawer unmounts its content when it closes, which would take the
   * terms dialog with it, so that one opens over the drawer instead.
   */
  onSelect: () => void
}

/**
 * The More Actions items (View Terms, Print, Download, Decline) listed under the pages in the phone
 * Pages drawer, because the customer view's phone bar has no "More…" menu (ENG-2075). The items and
 * their rules come from {@link usePresentationMoreActions}, the same source as the desktop menu.
 */
export default function PresentationDrawerActions({ onSelect }: PresentationDrawerActionsProps) {
  const classes = useStyles()
  const { actions, hasTerms, termsOpen, closeTerms } = usePresentationMoreActions()

  if (actions.length === 0) return null

  return (
    <>
      <div className={classes.root}>
        {actions.map(({ key, label, icon: Icon, onSelect: runAction }) => (
          <button
            key={key}
            type="button"
            className={classes.action}
            onClick={(ev) => {
              if (key !== 'terms') onSelect()
              runAction(ev)
            }}
          >
            <Icon className={classes.icon} />
            {label}
          </button>
        ))}
      </div>
      {hasTerms && <PresentationTermsDialog open={termsOpen} onClose={closeTerms} />}
    </>
  )
}
